import {
  ChangeDetectorRef,
  Component,
  Inject,
  OnInit,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { IFieldConfig } from 'src/app/domain/IFieldConfig';
import { TypeCesEnum } from 'src/app/domain/registration/enums/type-ces.enum';
import { ISystem } from 'src/app/domain/registration/models/ISystem';
import { PermissionService } from 'src/app/shared/permission.service';
import { CES_CONFIG_FIELDS } from '../crud-config';
import { AuthService } from 'src/app/views/auth/shared/auth.service';
import { SystemService } from '../crud-service';
import { SystemDueDateEnum, SystemTypeEnum, SystemTypeIdEnum } from 'src/app/domain/registration/enums/system-type.enum';
import { ToastTypeEnum } from 'src/app/shared/services/toast/toast-type-enum';
import { ToastService } from 'src/app/shared/services/toast/toast.service';
import { map, startWith } from 'rxjs/operators';

@Component({
  selector: 'app-system-ces',
  templateUrl: './crud-ces.component.html',
  styleUrls: ['./crud-ces.component.sass'],
})
export class SystemCESComponent implements OnInit {
  form: FormGroup;
  config: IFieldConfig[] = CES_CONFIG_FIELDS;
  data: ISystem;
  type: TypeCesEnum;
  title = '--';
  loadingTemplate = false;
  templateError = false;
  public showParentSystemSelect = false;

  constructor(
    public dialogRef: MatDialogRef<SystemCESComponent>,
    public permissionService: PermissionService,
    private authService: AuthService,
    private systemService: SystemService,
    private toastService: ToastService,
    @Inject(MAT_DIALOG_DATA)
    data: { type: TypeCesEnum; element: ISystem },
    private cdr: ChangeDetectorRef,
    private fb: FormBuilder
  ) {
    this.type = data.type;
    this.data = data.element;
    this.title = this.type;
  }

  get typeCesEnum() {
    return TypeCesEnum;
  }

  public get controls() {
    return this.config
      .filter(({ type }) => type !== 'button')
      .map((control) => {
        if (this.type === TypeCesEnum.SHOW) {
          control.disabled = true;
        } else {
          control.disabled = false;
        }
        return control;
      });
  }

  public createGroup() {
    const group = this.fb.group({});
    this.controls.forEach((control) =>
      group.addControl(control.name, this.createControl(control))
    );
    return group;
  }

  public createControl(config: IFieldConfig) {
    const { disabled, validation, value } = config;
    return this.fb.control({ disabled, value }, validation);
  }

  public getFieldConfig(name: string): any {
    const field = this.config?.find((c: any) => c.name === name);
    return field;
  }

  public isAllowChangeContract(): TypeCesEnum {
    let hasContract = this.form.get('contractTemplateId')?.value || this.form.get('contractTemplateUrl')?.value;
    return hasContract && this.authService.isPartner()
      ? TypeCesEnum.SHOW
      : this.type;
  }

  ngOnInit(): void {
    import('../../systems/crud-index/crud-index-select.component').then(({ SystemIndexSelectComponent }) => {
      const parentSystemConfig = this.config.find(c => c.name === 'parentSystem');
      if (parentSystemConfig) {
        parentSystemConfig.component = SystemIndexSelectComponent;
      }
    });
    this.form = this.createGroup();

    if (!this.form.get('contractTemplateUrl')) {
      this.form.addControl('contractTemplateUrl', this.fb.control(''));
    }
    if (!this.form.get('contractTemplateName')) {
      this.form.addControl('contractTemplateName', this.fb.control(''));
    }
    if (!this.form.get('contractTemplateType')) {
      this.form.addControl('contractTemplateType', this.fb.control(''));
    }
    if (!this.form.get('contractTemplateId')) {
      this.form.addControl('contractTemplateId', this.fb.control(null));
    }
    if (!this.form.get('parentSystem')) {
      this.form.addControl('parentSystem', this.fb.control(null));
    }

    if (this.data?.id) {
      this.loadSystemWithTemplate();
    } else {
      this.updateParentSystemFieldVisibility(this.form.get('type')?.value);
    }
  }

  loadSystemWithTemplate() {
    this.loadingTemplate = true;
    this.templateError = false;
    this.systemService.getSystemWithTemplate(this.data.id).subscribe({
      next: (system) => {
        this.data = system;
        this.setFields();
        this.loadingTemplate = false;
      },
      error: (err) => {
        console.error('❌ Erro ao carregar sistema com template:', err);
        this.toastService.showError('Erro ao carregar detalhes do sistema.');
        this.templateError = true;
        this.loadingTemplate = false;
        this.setFields();
      }
    });
  }

  setFields() {
    if (!this.data) {
      console.warn('⚠️ Tentou chamar setFields sem dados.');
      return;
    }
    const formValue: any = { ...this.data };

    if (formValue.dueDate !== undefined && formValue.dueDate !== null) {
      const dueDateValue = Number(formValue.dueDate);
      if (!isNaN(dueDateValue) && Object.values(SystemDueDateEnum).includes(dueDateValue)) {
        formValue.dueDate = dueDateValue;
      } else {
        console.warn(`⚠️ Valor de dueDate inválido ou não encontrado no enum: ${formValue.dueDate}. Será deixado em branco.`);
        formValue.dueDate = null;
      }
    } else {
      formValue.dueDate = null;
    }

    if (formValue.type !== undefined && typeof formValue.type === 'number') {
      const typeValue = formValue.type;
      const typeKey = Object.keys(SystemTypeIdEnum).find(
        key => SystemTypeIdEnum[key as keyof typeof SystemTypeIdEnum] === typeValue
      );
      if (typeKey) {
        formValue.type = typeKey;
      } else {
        console.error(`❌ Tipo (${typeValue}) não encontrado no enum SystemTypeIdEnum.`);
        formValue.type = null;
      }
    } else {
      console.warn(`⚠️ Tipo não é um número ou não definido: ${formValue.type}`);
      formValue.type = null;
    }

    if (this.data.parentSystemId) {
      if (this.type === TypeCesEnum.SHOW || (this.type === TypeCesEnum.EDIT && this.data.parentSystem && this.data.parentSystem.name)) {
        formValue.parentSystem = this.data.parentSystem?.name;
      } else {
        formValue.parentSystem = this.data.parentSystemId;
      }
    } else {
      formValue.parentSystem = null;
    }
    this.updateParentSystemFieldVisibility(formValue.type);

    let templateUrl = this.data.contractTemplateUrl || '';
    if (templateUrl && !templateUrl.startsWith('http')) {
      if (!templateUrl.includes('CONTRACT_TEMPLATES/') && this.data.contractTemplateId) {
        templateUrl = `CONTRACT_TEMPLATES/${templateUrl}`;
      }
    }
    formValue.contractTemplateUrl = templateUrl;
    formValue.contractTemplateName = this.data.contractTemplateName || 'Arquivo anexado';
    formValue.contractTemplateType = this.data.contractTemplateType || '';

    if (formValue.contractTemplateUrl || formValue.contractTemplateId) {
      formValue.contractTemplate = formValue.contractTemplateName;
    } else {
      formValue.contractTemplate = '';
    }

    this.form.patchValue(formValue);
    this.cdr.detectChanges();
  }

  hasTemplate(): boolean {
    return !!this.form.get('contractTemplateUrl')?.value || !!this.form.get('contractTemplateId')?.value;
  }

  ngAfterViewInit() {
    this.cdr.detectChanges();
    this.setupParentSystemLogic();
  }

  onSubmit(value: { [name: string]: any }) {
    this.dialogRef.close({ element: value });
  }

  onCancel() {
    this.dialogRef.close();
  }

  changeType(type: TypeCesEnum) {
    this.type = type;
    this.dialogRef.close({ changeType: type });
  }

  delete() {
    this.dialogRef.close({ delete: true });
  }

  private setupParentSystemLogic(): void {
    this.form.get('type')?.valueChanges.pipe(
      startWith(this.form.get('type')?.value)
    ).subscribe(typeValue => {
      this.updateParentSystemFieldVisibility(typeValue);
    });
  }

  private updateParentSystemFieldVisibility(systemType: string): void {
    const parentSystemControl = this.form.get('parentSystem');
    const parentSystemConfig = this.getFieldConfig('parentSystem');

    if (systemType === SystemTypeEnum.APPLICATION) {
      this.showParentSystemSelect = true;
      parentSystemControl?.enable();
      if (parentSystemConfig && parentSystemConfig.validation) {
        parentSystemControl?.setValidators(parentSystemConfig.validation);
      } else {
        parentSystemControl?.setValidators(Validators.required);
      }
    } else {
      this.showParentSystemSelect = false;
      parentSystemControl?.disable();
      parentSystemControl?.setValue(null);
      parentSystemControl?.clearValidators();
    }
    parentSystemControl?.updateValueAndValidity();
    this.cdr.detectChanges();
  }
}
